
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  noindex?: boolean;
  canonical?: string;
  structuredData?: object;
}

export default function SEOHead({
  title,
  description,
  keywords,
  image = 'https://static.readdy.ai/image/5c4d3156b18a91d26c6b2b068b2e3a44/1912de5b3ab345395afff437b1bfb754.jfif',
  type = 'website',
  noindex = false,
  canonical,
  structuredData
}: SEOHeadProps) {
  const location = useLocation();
  const { i18n } = useTranslation();
  
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://drixemgroup.com';
  const currentUrl = `${baseUrl}${location.pathname}`;
  const currentCanonical = canonical || currentUrl;
  
  // Default SEO values
  const defaultTitle = 'DRIXEM GROUP - Malaysia\'s Leading Palm Oil Specialists';
  const defaultDescription = 'Malaysia\'s premier palm oil specialists with 10+ years expertise. Government certified premium palm oil export, mill setup consultation, and OEM manufacturing services.';
  const defaultKeywords = 'Malaysian palm oil, RBD palm olein, CP6 CP8 CP10, JAKIM halal certified, MPOB licensed, palm oil export Malaysia';
  
  const finalTitle = title ? `${title} | Drixem Group` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;

  useEffect(() => {
    // Update document title
    document.title = finalTitle;
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', finalDescription);
    updateMetaTag('keywords', finalKeywords);
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Open Graph tags
    updateMetaTag('og:title', finalTitle, true);
    updateMetaTag('og:description', finalDescription, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'Drixem Group', true);
    updateMetaTag('og:locale', i18n.language === 'en' ? 'en_US' : `${i18n.language}_${i18n.language.toUpperCase()}`, true);
    
    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', finalTitle, true);
    updateMetaTag('twitter:description', finalDescription, true);
    updateMetaTag('twitter:image', image, true);
    updateMetaTag('twitter:url', currentUrl, true);
    
    // Language and alternate tags
    updateMetaTag('language', i18n.language);
    
    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = currentCanonical;
    
    // Update alternate language links
    const languages = ['en', 'ms', 'zh', 'ar', 'es', 'fr'];
    languages.forEach(lang => {
      let alternate = document.querySelector(`link[hreflang="${lang}"]`) as HTMLLinkElement;
      if (!alternate) {
        alternate = document.createElement('link');
        alternate.rel = 'alternate';
        alternate.hreflang = lang;
        document.head.appendChild(alternate);
      }
      alternate.href = lang === 'en' ? currentUrl : `${currentUrl}?lang=${lang}`;
    });
    
    // Add structured data if provided
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo-structured]');
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-structured', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
    
    // Add breadcrumb structured data for non-home pages
    if (location.pathname !== '/') {
      const breadcrumbScript = document.querySelector('script[data-breadcrumb]');
      if (breadcrumbScript) {
        breadcrumbScript.remove();
      }
      
      const pathSegments = location.pathname.split('/').filter(segment => segment);
      const breadcrumbItems = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl
        }
      ];
      
      pathSegments.forEach((segment, index) => {
        const name = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        const url = `${baseUrl}/${pathSegments.slice(0, index + 1).join('/')}`;
        
        breadcrumbItems.push({
          "@type": "ListItem",
          "position": index + 2,
          "name": name,
          "item": url
        });
      });
      
      const breadcrumbData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems
      };
      
      const newBreadcrumbScript = document.createElement('script');
      newBreadcrumbScript.type = 'application/ld+json';
      newBreadcrumbScript.setAttribute('data-breadcrumb', 'true');
      newBreadcrumbScript.textContent = JSON.stringify(breadcrumbData);
      document.head.appendChild(newBreadcrumbScript);
    }
    
  }, [finalTitle, finalDescription, finalKeywords, image, currentUrl, currentCanonical, type, noindex, structuredData, i18n.language, location.pathname]);

  return null;
}